import {
  BUCKET_INTERVAL,
  type BucketInterval,
} from "@/utils/date/timeBucketing"

import type { ChartDataPoint } from "../../types/chart"
import { formatChartDateLabel } from "./dateFormatting"

/**
 * Generate all bucket timestamps for a given date range and interval
 */
function generateBucketTimestamps(
  startDate: Date,
  endDate: Date,
  bucketInterval: BucketInterval,
): Date[] {
  const buckets: Date[] = []
  const current = new Date(startDate)

  const getNextBucket = (date: Date): Date => {
    const next = new Date(date)
    switch (bucketInterval) {
      case BUCKET_INTERVAL.FIFTEEN_MINUTES:
        next.setMinutes(next.getMinutes() + 15)
        break
      case BUCKET_INTERVAL.ONE_HOUR:
        next.setHours(next.getHours() + 1)
        break
      case BUCKET_INTERVAL.FOUR_HOURS:
        next.setHours(next.getHours() + 4)
        break
      case BUCKET_INTERVAL.ONE_DAY:
        next.setDate(next.getDate() + 1)
        break
    }
    return next
  }

  while (current <= endDate) {
    buckets.push(new Date(current))
    const next = getNextBucket(current)
    current.setTime(next.getTime())
  }

  return buckets
}

/**
 * Fill missing buckets in chart data with empty values
 * This ensures all time periods are represented on the x-axis
 */
export function fillMissingBuckets(
  data: ChartDataPoint[],
  startDate: Date,
  endDate: Date,
  bucketInterval: BucketInterval,
): ChartDataPoint[] {
  if (data.length === 0) {
    return []
  }

  // Generate all expected bucket timestamps
  const allBuckets = generateBucketTimestamps(
    startDate,
    endDate,
    bucketInterval,
  )

  // Create a map of existing data by bucket time (ISO string)
  const dataMap = new Map<string, ChartDataPoint>()
  data.forEach((point) => {
    const key = point.bucketTime || new Date(point.timestamp).toISOString()
    dataMap.set(key, point)
  })

  // Fill in missing buckets
  const filledData: ChartDataPoint[] = allBuckets.map((bucketDate) => {
    const bucketTimeKey = bucketDate.toISOString()
    const existingData = dataMap.get(bucketTimeKey)

    if (existingData) {
      return existingData
    }

    // Create empty bucket
    return {
      date: formatChartDateLabel(bucketDate, bucketInterval),
      timestamp: bucketDate.getTime(),
      bucketTime: bucketTimeKey,
      value: undefined, // undefined instead of 0 so Recharts doesn't draw a bar
    }
  })

  // The generated grid starts at the raw range start, while the server bins on interval boundaries, so
  // an off-grid range (a rolling "last 24 hours", a zoom selection) can match nothing at all. Keeping
  // the unmatched points is what stops that from rendering as an empty chart with a normal-looking axis.
  const generatedBucketKeys = new Set(
    allBuckets.map((bucketDate) => bucketDate.toISOString()),
  )
  const unmatchedData = data.filter(
    (point) =>
      !generatedBucketKeys.has(
        point.bucketTime || new Date(point.timestamp).toISOString(),
      ),
  )

  if (unmatchedData.length === 0) {
    return filledData
  }

  return [...filledData, ...unmatchedData].sort(
    (left, right) => left.timestamp - right.timestamp,
  )
}
